import { useMemo, useState, type ReactElement } from 'react';
import {
  useLoadByDiscipline,
  type DisciplinePmcDay,
} from '@/lib/polarization';
import {
  DISCIPLINE_COLORS,
  DISCIPLINE_ORDER,
  type DisciplineKey,
} from '@/lib/disciplineColors';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * DisciplinePmcChart — overlay de CTL por disciplina (M2.32.D).
 *
 * Líneas:
 *   - 6 líneas finas (CTL swim, bike, run, strength, brick, other) con la
 *     paleta canónica de `DISCIPLINE_COLORS`.
 *   - 1 línea gruesa "Total" (CTL global) — opcional, toggleable desde
 *     la leyenda.
 *
 * Cada disciplina tiene un toggle en la leyenda; al ocultar una, el resto
 * re-escala vertical (max recalculado).
 *
 * Eje X = tiempo (cada N días dependiendo del rango).
 * Eje Y = CTL (TSS/día).
 *
 * SVG inline para evitar bundle de chart libraries. Tooltip nativo via
 * `<title>` en círculos invisibles a lo largo de cada línea (pattern
 * estable, sin hover handlers ni state).
 */

const RANGE_OPTIONS = [
  { id: 90, label: '90 días' },
  { id: 180, label: '180 días' },
  { id: 365, label: '365 días' },
] as const;

const CHART_HEIGHT = 260;
const TOP_PADDING = 16;
const BOTTOM_PADDING = 28;
const LEFT_PADDING = 36;
const RIGHT_PADDING = 8;
const PLOT_HEIGHT = CHART_HEIGHT - TOP_PADDING - BOTTOM_PADDING;

type Visibility = Record<DisciplineKey | 'total', boolean>;

const DEFAULT_VISIBILITY: Visibility = {
  swim: true,
  bike: true,
  run: true,
  strength: true,
  brick: false,
  other: false,
  total: true,
};

function buildPath(
  days: DisciplinePmcDay[],
  picker: (d: DisciplinePmcDay) => number,
  width: number,
  maxY: number,
): string {
  if (days.length === 0 || width <= 0 || maxY <= 0) return '';
  const stepX = width / Math.max(1, days.length - 1);
  return days
    .map((d, i) => {
      const x = i * stepX;
      const y = TOP_PADDING + PLOT_HEIGHT - (picker(d) / maxY) * PLOT_HEIGHT;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

function maxCtl(days: DisciplinePmcDay[], visibility: Visibility): number {
  let max = 0;
  for (const d of days) {
    if (visibility.total && d.ctl > max) max = d.ctl;
    for (const k of DISCIPLINE_ORDER) {
      if (!visibility[k]) continue;
      const v = d.ctlByDiscipline[k];
      if (v > max) max = v;
    }
  }
  return max;
}

function formatDateLabel(dateISO: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateISO);
  if (!m) return dateISO;
  return `${m[3]}/${m[2]}`;
}

/**
 * Mapeo de disciplina → clave del glosario para tooltip de su CTL.
 * Solo swim/bike/run tienen entrada propia; las demás devuelven null y
 * el toggle de leyenda se queda sin tooltip.
 */
function disciplineCtlMetric(k: DisciplineKey): MetricKey | null {
  switch (k) {
    case 'swim':
      return 'sCtl';
    case 'bike':
      return 'bCtl';
    case 'run':
      return 'rCtl';
    default:
      return null;
  }
}

export function DisciplinePmcChart(): ReactElement {
  const [days, setDays] = useState<number>(180);
  const [visibility, setVisibility] = useState<Visibility>(DEFAULT_VISIBILITY);
  const query = useLoadByDiscipline(days);

  const data = query.data?.days ?? [];

  const chartWidth = useMemo(() => {
    return 720; // ancho fijo razonable; overflow-x-auto en wrapper
  }, []);
  const plotWidth = chartWidth - LEFT_PADDING - RIGHT_PADDING;
  const maxY = useMemo(() => Math.max(1, maxCtl(data, visibility)), [data, visibility]);

  // Etiquetas eje X: ~6 ticks distribuidas.
  const xTicks = useMemo(() => {
    if (data.length === 0) return [];
    const TICK_COUNT = 6;
    const stepX = plotWidth / Math.max(1, data.length - 1);
    const step = Math.max(1, Math.floor(data.length / TICK_COUNT));
    const ticks: Array<{ x: number; label: string }> = [];
    for (let i = 0; i < data.length; i += step) {
      ticks.push({
        x: LEFT_PADDING + i * stepX,
        label: formatDateLabel(data[i]!.dateISO),
      });
    }
    return ticks;
  }, [data, plotWidth]);

  // Etiquetas eje Y: 4 ticks (0, 25%, 50%, 75%, 100% del max).
  const yTicks = useMemo(() => {
    return [0, 0.25, 0.5, 0.75, 1].map((r) => ({
      ratio: r,
      value: r * maxY,
      y: TOP_PADDING + PLOT_HEIGHT * (1 - r),
    }));
  }, [maxY]);

  const toggle = (k: keyof Visibility) =>
    setVisibility((v) => ({ ...v, [k]: !v[k] }));

  return (
    <article
      data-testid="discipline-pmc-chart"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            PMC por disciplina
          </h3>
          <p className="text-xs text-slate-600">
            <MetricTooltip metric="ctl">CTL</MetricTooltip> desglosado: detecta si el chronic está dominado por una
            disciplina (overuse) o equilibrado.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Rango">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setDays(r.id)}
              aria-pressed={days === r.id}
              className={
                days === r.id
                  ? 'rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1'
                  : 'rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1'
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-sm text-slate-600">
          Cargando PMC…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-rose-700">
          No se pudo cargar el PMC por disciplina.
        </p>
      )}

      {query.data && data.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
          Sin datos. Ejecuta el recompute desde Carga para repoblar el histórico.
        </p>
      )}

      {data.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <svg
              role="img"
              aria-label={`Gráfico CTL por disciplina últimos ${days} días`}
              width={chartWidth}
              height={CHART_HEIGHT}
              data-testid="discipline-pmc-svg"
            >
              {/* Eje Y grid + labels */}
              {yTicks.map((t) => (
                <g key={`y-${t.ratio}`}>
                  <line
                    x1={LEFT_PADDING}
                    x2={chartWidth - RIGHT_PADDING}
                    y1={t.y}
                    y2={t.y}
                    stroke="#e2e8f0"
                    strokeWidth={1}
                  />
                  <text
                    x={LEFT_PADDING - 4}
                    y={t.y + 3}
                    fontSize="9"
                    fill="#64748b"
                    textAnchor="end"
                  >
                    {Math.round(t.value)}
                  </text>
                </g>
              ))}

              {/* Eje X labels */}
              {xTicks.map((t, i) => (
                <text
                  key={`x-${i}`}
                  x={t.x}
                  y={CHART_HEIGHT - 8}
                  fontSize="9"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {t.label}
                </text>
              ))}

              {/* Líneas por disciplina */}
              <g transform={`translate(${LEFT_PADDING},0)`}>
                {DISCIPLINE_ORDER.map((k) => {
                  if (!visibility[k]) return null;
                  const color = DISCIPLINE_COLORS[k].hex;
                  const path = buildPath(
                    data,
                    (d) => d.ctlByDiscipline[k],
                    plotWidth,
                    maxY,
                  );
                  return (
                    <path
                      key={k}
                      d={path}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.5}
                      data-testid={`pmc-line-${k}`}
                    />
                  );
                })}
                {/* Total overlay (línea más gruesa, negro) */}
                {visibility.total && (
                  <path
                    d={buildPath(data, (d) => d.ctl, plotWidth, maxY)}
                    fill="none"
                    stroke="rgb(240 240 245)"
                    strokeWidth={2.5}
                    strokeOpacity={0.85}
                    data-testid="pmc-line-total"
                  />
                )}
              </g>

              {/* Puntos invisibles para tooltip al final del rango */}
              {data.length > 0 && visibility.total && (
                <circle
                  cx={LEFT_PADDING + plotWidth}
                  cy={TOP_PADDING + PLOT_HEIGHT - (data[data.length - 1]!.ctl / maxY) * PLOT_HEIGHT}
                  r={3}
                  fill="rgb(240 240 245)"
                >
                  <title>{`CTL total hoy: ${data[data.length - 1]!.ctl.toFixed(1)}`}</title>
                </circle>
              )}
            </svg>
          </div>

          {/* Leyenda con toggles */}
          <div
            className="mt-3 flex flex-wrap items-center gap-2 text-xs"
            data-testid="discipline-pmc-legend"
            role="group"
            aria-label="Toggle de disciplinas"
          >
            {DISCIPLINE_ORDER.map((k) => {
              const active = visibility[k];
              const color = DISCIPLINE_COLORS[k].hex;
              const ctlMetric = disciplineCtlMetric(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggle(k)}
                  aria-pressed={active}
                  data-testid={`pmc-toggle-${k}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-opacity ${
                    active
                      ? 'border-slate-300 bg-white text-slate-700'
                      : 'border-slate-200 bg-slate-50 text-slate-400'
                  }`}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: color, opacity: active ? 1 : 0.35 }}
                    aria-hidden="true"
                  />
                  {ctlMetric ? (
                    <MetricTooltip metric={ctlMetric}>
                      {DISCIPLINE_COLORS[k].label}
                    </MetricTooltip>
                  ) : (
                    DISCIPLINE_COLORS[k].label
                  )}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => toggle('total')}
              aria-pressed={visibility.total}
              data-testid="pmc-toggle-total"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-opacity ${
                visibility.total
                  ? 'border-slate-300 bg-white text-slate-700'
                  : 'border-slate-200 bg-slate-50 text-slate-400'
              }`}
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full bg-slate-900"
                style={{ opacity: visibility.total ? 1 : 0.35 }}
                aria-hidden="true"
              />
              Total
            </button>
          </div>
        </>
      )}
    </article>
  );
}
